import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import PageHeader, {ActionButton} from "../../components/layout/page-header";
import DocumentList from "../../module/document/document-list";
import ConnectionDocumentList from "../../module/document/connection-document-list";
import {
    CUSTOMER_QUOTATION_TYPE,
    resolveDocumentPermissionGroup,
    resolveDocumentSlugFromType,
    resolveDocumentTypeFromSlug,
} from "../../module/document/constants";
import * as Endpoint from "../endpoints";
import {PlusIcon} from "@heroicons/react/outline";
import DocumentCreatePopup from "../../module/document/components/document-create-popup";
import BasePermissionWrapper, {
    hasPermission,
    hasPermissionFor,
} from "../../components/partials/restricted/base-permission-wrapper";
import {
    getPermission,
    permissionGroup,
    permissionType,
} from "../../constants/permissions";
import {COMPANY_IMPORTER} from "../../constants/config";
import {fetchDocumentTypes} from "../../reducers/configReducer";

const buildListPath = (slug) => `${Endpoint.SALES + Endpoint.DOCUMENTS_PAGE}/${slug}`;
const buildDetailPath = (slug, documentId) => `${buildListPath(slug)}/${documentId}`;

const DocumentsPage = () => {
    const {document_type: routeSlug} = useParams();
    const navigate = useNavigate();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const type = useMemo(() => resolveDocumentTypeFromSlug(routeSlug), [routeSlug]);
    const slug = useMemo(() => routeSlug ?? resolveDocumentSlugFromType(type), [routeSlug, type]);
    const [isCreateOpen, setCreateOpen] = useState(false);

    const {documentTypesList = []} = useSelector((state) => state.config ?? {});
    const {companyType} = useSelector((state) => state.user ?? {});
    const {permissions} = useSelector((state) => state.userCompany ?? {permissions: {}});

    const documentPermissionGroup = useMemo(
        () => resolveDocumentPermissionGroup({type, slug}) ?? permissionGroup.DOCUMENT,
        [slug, type],
    );

    const canList = hasPermissionFor(permissions, documentPermissionGroup, permissionType.LIST);
    const canViewDetail = hasPermissionFor(permissions, documentPermissionGroup, permissionType.VIEW);
    const canCreate = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionType.CREATE,
        companyType,
        true,
    );
    const canEdit = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionType.EDIT,
        companyType,
        true,
    );
    const canDelete = hasPermission(
        permissions,
        documentPermissionGroup,
        permissionType.DELETE,
        companyType,
        true,
    );
    useEffect(() => {
        dispatch(fetchDocumentTypes());
    }, [dispatch]);

    const defaultDocumentsTitle = t("app.documents.documents", {defaultValue: "Documents"});
    const supplierQuotationsTitle = t("app.quotations.supplier_quotations", {defaultValue: "Supplier Quotations"});
    const typeLabelFromConfig = documentTypesList.find(
        (documentType) =>
            documentType?.code === type ||
            String(documentType?.value ?? "") === type ||
            documentType?.reference === type,
    )?.label;
    const formattedTypeTitle = useMemo(() => {
        if (!type) {
            return defaultDocumentsTitle;
        }
        if (type === CUSTOMER_QUOTATION_TYPE) {
            return t("app.quotations.quotations", {defaultValue: "Quotations"});
        }
        return type
            .split("-")
            .filter(Boolean)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(" ");
    }, [defaultDocumentsTitle, t, type]);
    const isImporterView = companyType === COMPANY_IMPORTER;

    const pageTitle = useMemo(() => {
        if (isImporterView) {
            return supplierQuotationsTitle;
        }
        return typeLabelFromConfig ?? formattedTypeTitle ?? defaultDocumentsTitle;
    }, [defaultDocumentsTitle, formattedTypeTitle, isImporterView, supplierQuotationsTitle, typeLabelFromConfig]);
    const canNavigate = Boolean(slug);

    const handleViewDocument = canViewDetail
        ? (document) => navigate(buildDetailPath(slug, document.id))
        : undefined;
    const handleEditDocument = canEdit
        ? (document) => navigate(buildDetailPath(slug, document.id))
        : undefined;

    return (
        <BasePermissionWrapper
            requiredPermissions={[getPermission(documentPermissionGroup, permissionType.LIST)]}
            renderBaseOnFail={true}
        >
            <div className="pt-4 bg-white overflow-hidden relative ">
                <div className="mx-auto max-w-full px-2 -mb-1">
                    <PageHeader title={pageTitle} canSwitch={true}>
                        {canCreate && type && canNavigate && (
                            <ActionButton
                                icon={<PlusIcon className="h-4 w-4"/>}
                                onClick={() => setCreateOpen(true)}
                                text={t("app.document.new_document", {defaultValue: "New document"})}
                            />
                        )}
                    </PageHeader>
                </div>

                <div className="mx-auto max-w-full ">
                    {canList && type && canNavigate && (
                        isImporterView ? (
                            <ConnectionDocumentList
                                type={type}
                                title={pageTitle}
                                onViewDocument={handleViewDocument}
                            />
                        ) : (
                            <DocumentList
                                type={type}
                                onViewDocument={handleViewDocument}
                                onEditDocument={handleEditDocument}
                                canEdit={canEdit}
                                enableDelete={canDelete}
                            />
                        )
                    )}
                </div>

                {canCreate && type && canNavigate && (
                    <DocumentCreatePopup
                        isOpen={isCreateOpen}
                        onClose={() => setCreateOpen(false)}
                        type={type}
                        onSuccess={(response) => {
                            const createdId = response?.id ?? response?.document?.id ?? null;
                            setCreateOpen(false);
                            if (createdId) {
                                navigate(buildDetailPath(slug, createdId));
                            } else if (slug) {
                                navigate(buildListPath(slug));
                            }
                        }}
                    />
                )}
            </div>
        </BasePermissionWrapper>
    );
};

export default DocumentsPage;
